import { useQuery } from "@tanstack/react-query";

import { accounting } from "@/api/accounting";
import { Badge, Card, CardBody, CardHeader, EmptyState } from "@/components/ui";

/**
 * General ledger — double-entry chart of accounts + recent journal entries.
 * Read-only; entries are posted by the settlement/funding daemons.
 */
export function AdminGlPage() {
  const accounts = useQuery({ queryKey: ["gl-accounts"], queryFn: () => accounting.glAccounts() });
  const entries = useQuery({ queryKey: ["gl-entries"], queryFn: () => accounting.glEntries() });

  return (
    <div className="space-y-3">
      <h1 className="text-base font-semibold text-term-fg">General ledger</h1>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Chart of accounts</span>
        </CardHeader>
        <CardBody className="p-0">
          {accounts.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !accounts.data || accounts.data.length === 0 ? (
            <EmptyState title="No accounts" description="GL accounts appear here once the ledger is seeded." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Code</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Label</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Kind</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Currency</th>
                </tr>
              </thead>
              <tbody>
                {accounts.data.map((a) => (
                  <tr key={a.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{a.code}</td>
                    <td className="px-3 py-1.5 text-term-dim">{a.label}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone="neutral">{a.kind}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-term-dim">{a.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Recent journal entries</span>
        </CardHeader>
        <CardBody className="p-0">
          {entries.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !entries.data || entries.data.length === 0 ? (
            <EmptyState title="No entries" description="Posted journal entries appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">When</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Kind</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Amount</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Dr → Cr</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Reference</th>
                </tr>
              </thead>
              <tbody>
                {entries.data.map((e) => (
                  <tr key={e.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {e.occurred_at ? new Date(e.occurred_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{e.kind}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {e.amount} {e.currency}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {e.debit_account_id ? e.debit_account_id.slice(0, 6) : "—"} →{" "}
                      {e.credit_account_id ? e.credit_account_id.slice(0, 6) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{e.reference ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
